// Demo Actions-tab data + its € math, shared by StoralyticDemoGuidance (the Actions tab)
// and the demo home's "Value delivered", so the two can never show different figures.
// Production does the same server-side: coach/guidance.py::rollup_measured mirrors
// GuidanceTab's run-rate + earned. (Moved verbatim out of StoralyticDemoGuidance.vue.)
import { demoDaysAgo as _demoDaysAgo, demoDateStr as _demoDateStr } from '@/api/demoData.js'
// The sample text (what was changed, the weather caveat) carries its Dutch (withNl).
import { withNl } from '@/demo/demoLang'

// IMPORTANT: every date offset in THIS file is relative to the tracked-action log's own
// "today" (authored as 2026-07-01), which sits 5 days BEFORE the file's newest date (a
// weather valid-through that's 5 days ahead of "today"). These wrappers shift all offsets
// back 5 days so the log anchors to demo-today: tracked series end at demo-today (not 5
// days short) and the forecast valid-through stays in the future. So a call like
// demoDateStr(7) here means "7 days before the log's today", i.e. _demoDateStr(2).
export const demoDaysAgo = (n) => _demoDaysAgo(n - 5)
export const demoDateStr = (n) => _demoDateStr(n - 5)

// The demo runs against a demo-relative "today" (5 days before the log's newest
// entry) so the 42-days / 9-days storylines stay exact whenever it's viewed. The
// real page uses the live date.
export const TODAY = demoDaysAgo(5)
export const DAY = 86400000

// Local-time ISO (never toISOString — that shifts to UTC and can roll the date
// back a day in +01/+02 timezones, which made the default land on yesterday).
export const pad2 = (n) => String(n).padStart(2, '0')
export const iso = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
export const addDays = (d, n) => new Date(d.getTime() + n * DAY)
export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// Deterministic pseudo-random so charts are stable across reloads.
export function mulberry(seed) {
  return function () {
    let t = (seed += 0x6d2b79f5)
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

// Build a daily series [pivot-elapsed, today] that steps from `before` to `after`
// at the pivot, with a little day-to-day and weekly wobble. Returns
// [{ dateISO, label, value, side }].
export function makeSeries(pivotISO, elapsed, before, after, seed) {
  const rnd = mulberry(seed)
  const pivot = new Date(pivotISO + 'T00:00:00')
  const start = addDays(pivot, -elapsed)
  const nDays = elapsed * 2 // equal window on each side (after runs pivot..today)
  const out = []
  for (let i = 0; i < nDays; i++) {
    const d = addDays(start, i)
    const isAfter = d >= pivot
    const base = isAfter ? after : before
    const weekly = 1 + 0.12 * Math.sin((i / 7) * Math.PI * 2) // gentle weekly rhythm
    const noise = 0.9 + rnd() * 0.2
    out.push({
      dateISO: iso(d),
      label: `${d.getDate()} ${MONTHS[d.getMonth()]}`,
      value: Math.round(base * weekly * noise * 10) / 10,
      side: isAfter ? 'after' : 'before',
    })
  }
  return out
}

// Build a { metric: [points] } set. `means` maps metric -> [before, after].
export function seriesSet(pivotISO, elapsed, means, seed) {
  const out = {}
  let i = 0
  for (const m of Object.keys(means)) {
    out[m] = makeSeries(pivotISO, elapsed, means[m][0], means[m][1], seed + i * 137)
    i++
  }
  return out
}

// Per-zone economics — in production these ARE zone settings (AverageProductValue,
// EngagersConversionRate). avgValue = average basket €; convEstimate = the modelled
// conversion used when no sales are fed (same value the gauges/€-potential use).
export const ZONE_ECON = {
  'Power Tools': { avgValue: 78, convEstimate: 0.46 },
  'Safety Shoes': { avgValue: 95, convEstimate: 0.44 },
  'Garden Utilities': { avgValue: 55, convEstimate: 0.40 },
  'Promoted Articles Zone': { avgValue: 48, convEstimate: 0.14 },
}
// Queue-elasticity model for checkout waits (mirrors computeServiceGauge):
// less excess wait → smaller lost-sales fraction of the store's realized revenue.
export const SERVICE_ECON = { serviceBaseDay: 3800, targetWaitSec: 60, kPerMin: 0.06, maxLoss: 0.15 }
export const IMPACT_FLOOR_EUR = 30 // below this monthly € we call it "no measurable effect"

export const meanSide = (arr, side) => {
  const xs = (arr || []).filter((p) => p.side === side).map((p) => p.value)
  return xs.length ? xs.reduce((s, x) => s + x, 0) / xs.length : null
}

export const elapsedDays = (a) =>
  Math.round((TODAY - new Date(a.pivot + 'T00:00:00')) / DAY)
export const isVerdict = (a) => elapsedDays(a) >= 28

// ---------------- Revenue impact (€) ----------------
// Same funnel/service math the gauges use, applied to the before/after drivers.
// Returns { eur, low, high, basis, confidence } — confidence gates whether we
// dare show a hard number: 'good' (measured) / 'indicative' (modelled) /
// 'early' (range only, too soon to bank) / 'none' (no measurable €) / 'na'.
export const finishImpact = (eur, basis, mature) => {
  if (Math.abs(eur) < IMPACT_FLOOR_EUR) return { eur: 0, basis, confidence: 'none' }
  const spread = !mature ? 0.4 : basis === 'measured' ? 0.12 : 0.25
  const conf = !mature ? 'early' : basis === 'measured' ? 'good' : 'indicative'
  return { eur, low: eur * (1 - spread), high: eur * (1 + spread), basis, confidence: conf }
}

export const computeImpact = (a) => {
  const s = a.series || {}
  const mature = isVerdict(a)

  // Funnel engine — product zones (visitors × engagement × conversion × basket)
  const econ = ZONE_ECON[a.scope]
  // A conversion series is NOT required: without sales the zone's modelled
  // conversion values the change (estimated) — the same rule production uses.
  if (s.engagement && econ) {
    const vB = meanSide(s.traffic, 'before')
    const vA = meanSide(s.traffic, 'after')
    const eB = meanSide(s.engagement, 'before')
    const eA = meanSide(s.engagement, 'after')
    if ([vB, vA, eB, eA].some((x) => x == null) || econ.avgValue == null)
      return { confidence: 'na' }
    const convR = a.readings.find((r) => r.metric === 'conversion')
    let cB, cA, basis
    if (a.salesProvided && convR && !convR.pending) {
      cB = meanSide(s.conversion, 'before') / 100
      cA = meanSide(s.conversion, 'after') / 100
      basis = 'measured'
    } else {
      cB = cA = econ.convEstimate // no sales → the zone's modelled conversion
      basis = 'estimated'
    }
    const day = (vA * (eA / 100) * cA - vB * (eB / 100) * cB) * econ.avgValue
    return finishImpact(day * 30, basis, mature)
  }

  // Service engine — checkout waiting (recovered lost-sales from shorter queues)
  if (s.wait) {
    const wB = meanSide(s.wait, 'before')
    const wA = meanSide(s.wait, 'after')
    if (wB == null || wA == null) return { confidence: 'na' }
    const { serviceBaseDay, targetWaitSec, kPerMin, maxLoss } = SERVICE_ECON
    const loss = (w) => Math.min(kPerMin * Math.max(0, (w - targetWaitSec) / 60), maxLoss)
    const day = serviceBaseDay * (loss(wB) - loss(wA))
    return finishImpact(day * 30, 'service', mature)
  }

  return { confidence: 'na' } // traffic-only / store without sales — can't ground €
}

// ---------------- Tracked actions (mock) ----------------
// ---------------- Tracked actions (mock) ----------------
// Story 1: a mature action with a real verdict. Story 2: a recent one that's
// still only a trajectory (< 28 days).
export function buildDemoTracked() {
  return [
  withNl({
    id: 'tr1',
    kind: 'money',
    scope: 'Power Tools',
    category: 'Power Tools',
    what: 'Set up a plug-in demo unit by the high-ticket range, with a spec-and-price card and a "call an expert" button beside it.',
    pivot: demoDateStr(47),
    goal: 'conversion',
    focus: 'conversion',
    series: seriesSet(
      demoDateStr(47),
      42,
      { traffic: [156, 170], dwell: [40, 46], engagement: [47, 50], conversion: [39, 46] },
      1000
    ),
    readings: [
      { metric: 'conversion', deltaPct: 17.9 },
      { metric: 'engagement', deltaPct: 6.4 },
      { metric: 'dwell', deltaPct: 15.0 },
      { metric: 'traffic', deltaPct: 9.0 },
    ],
    // Sales have been fed for the Power Tools category, so conversion is measured —
    // the experiment that turned Power Tools into the store's strongest zone.
    salesProvided: true,
    showSales: false,
    salesLog: [
      { date: demoDateStr(7), transactions: 26, revenue: 2028 },
      { date: demoDateStr(8), transactions: 22, revenue: 1716 },
    ],
    saleDraft: { date: demoDateStr(5), transactions: null, revenue: null },
  }, {
    what: 'Een demotoestel met stekker bij het dure gereedschap gezet, met een kaart met specificaties en prijs en een knop "roep een expert" ernaast.',
  }),
  withNl({
    id: 'tr4',
    kind: 'money',
    scope: 'Garden Utilities',
    category: 'Garden Utilities',
    what: 'Moved the bagged-compost pallet and a watering-can display to the mouth of the aisle.',
    pivot: demoDateStr(33),
    goal: 'reach',
    focus: 'reach',
    series: seriesSet(
      demoDateStr(33),
      28,
      { reach: [13.6, 17.5], traffic: [150, 192], dwell: [30, 33], engagement: [40, 42] },
      5000
    ),
    readings: [
      { metric: 'reach', deltaPct: 28.7 },
      { metric: 'traffic', deltaPct: 28.0 },
      { metric: 'dwell', deltaPct: 10.0 },
      { metric: 'engagement', deltaPct: 5.0 },
    ],
    salesProvided: false,
    showSales: false,
    salesLog: [],
    saleDraft: { date: demoDateStr(5), transactions: null, revenue: null },
    // Garden is the store's most weather-led zone and this window ran warm → the
    // traffic win is partly confounded by weather. Drives #3 (the verdict caveat).
    weatherCaveat: 'Garden leans on warm weather, and this ran through a warm, dry June — so some of that +28% footfall may be the weather, not the display. Worth another look once conditions cool before you bank it.',
  }, {
    what: 'De pallet met zakken potgrond en een display met gieters naar het begin van het gangpad verplaatst.',
    weatherCaveat: 'Garden leunt sterk op warm weer, en deze periode viel in een warme, droge junimaand — dus een deel van die +28% bezoekers kan aan het weer liggen, niet aan de display. Bekijk het nog eens als het koeler wordt, voordat je het als winst rekent.',
  }),
  withNl({
    id: 'tr3',
    kind: 'waiting',
    scope: 'Checkout 1 Waiting',
    what: 'Added a second lane on weekday late afternoons.',
    pivot: demoDateStr(40),
    goal: 'wait',
    focus: 'wait',
    series: seriesSet(
      demoDateStr(40),
      34,
      { wait: [92, 74], wait_rate: [44, 36], traffic: [120, 126] },
      7000
    ),
    readings: [
      { metric: 'wait', deltaPct: -19.6 }, // wait DOWN = a win
      { metric: 'wait_rate', deltaPct: -18.2 }, // fewer people had to queue at all
      { metric: 'traffic', deltaPct: 5.0 },
    ],
  }, {
    what: 'Op weekdagen aan het eind van de middag een tweede kassa geopend.',
  }),
  withNl({
    id: 'tr2',
    kind: 'money',
    scope: 'Promoted Articles Zone',
    category: 'Promoted Articles',
    what: 'Angled the promo fixture toward the main aisle.',
    pivot: demoDateStr(14),
    goal: 'engagement',
    focus: 'engagement',
    series: seriesSet(
      demoDateStr(14),
      9,
      { traffic: [41, 42], dwell: [17, 19.5], engagement: [38, 42], conversion: [14, 14.6] },
      3000
    ),
    // Engagement is up — the fixture change worked. Conversion is watched but no
    // sales fed yet → pending; closing that gap is the open suggestion (sg6).
    readings: [
      { metric: 'engagement', deltaPct: 10.5 },
      { metric: 'dwell', deltaPct: 12.4 },
      { metric: 'conversion', deltaPct: null, pending: true },
      { metric: 'traffic', deltaPct: 1.8 },
    ],
    salesProvided: false,
    showSales: false,
    salesLog: [],
    saleDraft: { date: demoDateStr(5), transactions: null, revenue: null },
  }, {
    what: 'De promo-opstelling naar het hoofdpad gedraaid.',
  }),
]
}

// The Actions tab's two headline figures for the seed list, computed exactly as the
// tab computes them (rollup.measured + earned.measured) — the demo home reads these.
//   runRate    €/mo  Σ eur of measured ('good') actions
//   cumulative €     Σ eur × elapsedDays / 30 over the same actions
//   since      ISO   earliest pivot among them
export function demoValueDelivered(tracked = buildDemoTracked()) {
  let runRate = 0, cumulative = 0, since = null
  for (const a of tracked) {
    const imp = computeImpact(a)
    if (imp.confidence !== 'good') continue
    runRate += imp.eur
    cumulative += ((imp.eur || 0) * elapsedDays(a)) / 30
    if (!since || a.pivot < since) since = a.pivot
  }
  return runRate || cumulative ? { runRate, cumulative, since } : null
}
